import type { ColumnState, GridApi } from "ag-grid-community";

export const BENEFITS_COLUMN_ID = "benefit.label";

type ColumnStateApi = Pick<GridApi, "applyColumnState" | "getColumnState"> &
  Partial<Pick<GridApi, "getDisplayedLeftColumns">>;

const applyingApis = new WeakSet<ColumnStateApi>();

const hasColumnStateApi = (api: unknown): api is ColumnStateApi =>
  typeof (api as ColumnStateApi | undefined)?.getColumnState === "function" &&
  typeof (api as ColumnStateApi | undefined)?.applyColumnState === "function";

const getFirstDisplayedLeftColumnId = (
  api: ColumnStateApi,
): string | undefined => {
  const leftColumns = api.getDisplayedLeftColumns?.();
  return leftColumns?.[0]?.getColId();
};

const isBenefitsColumnFirst = (
  api: ColumnStateApi,
  columnState: ColumnState[],
  benefitsColumnState: ColumnState,
): boolean => {
  const firstDisplayedLeftColumnId = getFirstDisplayedLeftColumnId(api);
  const firstColumnId = firstDisplayedLeftColumnId ?? columnState[0]?.colId;

  return (
    firstColumnId === BENEFITS_COLUMN_ID &&
    benefitsColumnState.pinned === "left" &&
    benefitsColumnState.hide !== true
  );
};

const clearApplyingApi = (api: ColumnStateApi): void => {
  queueMicrotask(() => applyingApis.delete(api));
};

export const normalizeBenefitsColumnState = (
  columnState: ColumnState[],
): ColumnState[] => {
  const benefitsColumnState = columnState.find(
    (column) => column.colId === BENEFITS_COLUMN_ID,
  );

  if (!benefitsColumnState) {
    return columnState;
  }

  return [
    {
      ...benefitsColumnState,
      hide: false,
      pinned: "left",
    },
    ...columnState
      .filter((column) => column.colId !== BENEFITS_COLUMN_ID)
      .map((column) => ({
        ...column,
        pinned: null,
      })),
  ];
};

const hasOnlyBenefitsColumnPinned = (columnState: ColumnState[]): boolean =>
  columnState.every(
    (column) => column.colId === BENEFITS_COLUMN_ID || column.pinned == null,
  );

export const enforceBenefitsColumnFirst = (api: unknown): void => {
  if (!hasColumnStateApi(api)) {
    return;
  }

  if (applyingApis.has(api)) {
    return;
  }

  const columnState = api.getColumnState();
  const benefitsColumnState = columnState.find(
    (column) => column.colId === BENEFITS_COLUMN_ID,
  );

  if (!benefitsColumnState) {
    return;
  }

  if (
    isBenefitsColumnFirst(api, columnState, benefitsColumnState) &&
    hasOnlyBenefitsColumnPinned(columnState)
  ) {
    return;
  }

  applyingApis.add(api);
  try {
    api.applyColumnState({
      state: normalizeBenefitsColumnState(columnState),
      applyOrder: true,
    });
  } finally {
    clearApplyingApi(api);
  }
};
